/**
 * 廠商操作台(REQ-20260522 Phase 1B-Console)。
 *
 * 取代舊 SupplierListPage / SupplierFormPage 二頁,合併到單頁操作台:
 * - 頂部 toolbar:+ 新增 / 儲存 / 刪除 / 取消 / 上一筆 / 下一筆 / 搜尋(REQ-20260916005)
 * - 中段:廠商欄位 (SupplierForm)
 * - 下段:分頁列(提醒事項 / 訪談紀錄 / 應付沖帳),REQ-20260918002 起與客戶操作台同一排
 * - 結構參考 CustomerConsolePage,廠商沒 owner / 沒 pin,UI 簡化
 *
 * URL:
 *   /suppliers/console?mode=new
 *   /suppliers/console?id=<n>&sortBy=<field>&order=asc|desc
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CreateSupplierInput, SupplierSortableField } from '@app/shared';
import { SUPPLIER_SORTABLE_FIELDS } from '@app/shared';
import {
  useCreateSupplier,
  useDeleteSupplier,
  useSupplier,
  useSupplierNeighbor,
  useSuppliers,
  useUpdateSupplier,
} from '../features/suppliers/api';
import {
  SupplierForm,
  type SupplierFormHandle,
} from '../features/suppliers/SupplierForm';
import { SupplierSearchModal } from '../features/suppliers/SupplierSearchModal';
import { SupplierApPanel } from '../features/settlements/SupplierApPanel';
import { InterviewTimeline } from '../features/interviews/InterviewTimeline';
import { useInterviewsTotalAll } from '../features/interviews/api';
import { ReminderPanel } from '../features/party-reminders/ReminderPanel';
import { useOpenReminders } from '../features/party-reminders/api';
import { TabButton } from '../components/TabButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { getApiErrorMessage } from '../lib/api-client';

const DEFAULT_SORT: SupplierSortableField = 'updatedAt';

function parseSort(raw: string | null): SupplierSortableField {
  if (raw && (SUPPLIER_SORTABLE_FIELDS as readonly string[]).includes(raw)) {
    return raw as SupplierSortableField;
  }
  return DEFAULT_SORT;
}

export function SupplierConsolePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode') === 'new' ? 'new' : 'view';
  const rawId = searchParams.get('id');
  const id = rawId ? Number(rawId) : undefined;
  const sortBy = parseSort(searchParams.get('sortBy'));
  const order: 'asc' | 'desc' = searchParams.get('order') === 'asc' ? 'asc' : 'desc';

  const formRef = useRef<SupplierFormHandle>(null);
  const [dirty, setDirty] = useState(false);
  // REQ-20260918002:改成與客戶操作台同一排分頁。切換廠商時重置回訪談紀錄
  // (寫法沿用 CustomerConsolePage;分頁狀態刻意**不進 URL** —— 那是 REQ-20260421010
  //  的既有決定,避免與 sortBy / id 等既有 query 互相打架)
  const [activeTab, setActiveTab] = useState<'reminders' | 'interviews' | 'ap'>('interviews');
  useEffect(() => setActiveTab('interviews'), [id]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const interviewsTotalQuery = useInterviewsTotalAll(mode === 'view' ? id : undefined, 'supplier');
  const openRemindersQuery = useOpenReminders('supplier', mode === 'view' ? id : undefined);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  // 每按一次「+ 新增」就 +1,拿來當 SupplierForm 的 key ——
  // 已經在新增模式時 defaultValues 兩次都是 undefined(參照沒變),
  // form 內的 useEffect 不會重跑,只有換 key 強制重新掛載才能清掉殘留輸入。
  const [newFormSeq, setNewFormSeq] = useState(0);
  // REQ-20260916004:檢視模式按「取消」時 +1,把 SupplierForm 重新掛載 = 還原成
  // react-query 快取裡的那一筆。**不能**改走 formRef 的 reset() ——
  // SupplierFormHandle 沒有那支,而且 rhf 無參數 reset() 會回到「最後一次
  // reset(values) 傳入的值」而不是空白(REQ-20260803002 已記過這個坑)。
  const [viewFormSeq, setViewFormSeq] = useState(0);
  // 「取消」在新增模式要退回剛剛看的那一筆,所以要記住它
  const lastViewedIdRef = useRef<number | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  // 沒指定 id 且非 new → 自動載入第一筆
  const firstQuery = useSuppliers({ page: 1, pageSize: 1, sort: sortBy, order });
  useEffect(() => {
    if (mode === 'new') return;
    if (id != null) return;
    const first = firstQuery.data?.data?.[0];
    if (first) {
      const next = new URLSearchParams(searchParams);
      next.set('id', String(first.id));
      next.set('sortBy', sortBy);
      next.set('order', order);
      setSearchParams(next, { replace: true });
    }
  }, [mode, id, firstQuery.data, searchParams, setSearchParams, sortBy, order]);

  useEffect(() => {
    if (mode !== 'view' || id == null) return;
    lastViewedIdRef.current = id;
  }, [mode, id]);

  const supplierQuery = useSupplier(mode === 'view' ? id : undefined);
  const current = supplierQuery.data;

  const prevQuery = useSupplierNeighbor(mode === 'view' ? id : undefined, {
    direction: 'previous',
    sortBy,
    order,
  });
  const nextQuery = useSupplierNeighbor(mode === 'view' ? id : undefined, {
    direction: 'next',
    sortBy,
    order,
  });

  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier(id ?? 0);
  const deleteMutation = useDeleteSupplier();

  const goTo = (nextId: number): void => {
    const next = new URLSearchParams(searchParams);
    next.delete('mode');
    next.set('id', String(nextId));
    next.set('sortBy', sortBy);
    next.set('order', order);
    setSearchParams(next);
  };

  const confirmLeave = (): boolean => {
    if (!dirty) return true;
    return window.confirm('有未儲存的變更,是否放棄?');
  };

  const handlePrev = (): void => {
    const nid = prevQuery.data?.id;
    if (nid == null) return;
    if (!confirmLeave()) return;
    goTo(nid);
  };
  const handleNext = (): void => {
    const nid = nextQuery.data?.id;
    if (nid == null) return;
    if (!confirmLeave()) return;
    goTo(nid);
  };

  const handleSave = (): void => {
    formRef.current?.submit();
  };

  const handleSubmit = (values: CreateSupplierInput): void => {
    setSubmitError(null);
    setSavedFlash(null);
    if (mode === 'new') {
      createMutation.mutate(values, {
        onSuccess: (created) => {
          setDirty(false);
          goTo(created.id);
        },
        onError: (e) => setSubmitError(getApiErrorMessage(e, '建立失敗')),
      });
    } else if (id != null) {
      updateMutation.mutate(values, {
        onSuccess: () => {
          setDirty(false);
          setSavedFlash('已儲存');
        },
        onError: (e) => setSubmitError(getApiErrorMessage(e, '更新失敗')),
      });
    }
  };

  const handleDelete = (): void => {
    if (mode !== 'view' || id == null || !current) return;
    if (!window.confirm(`確定刪除「${current.simpleName}」?資料會軟刪除可恢復。`)) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        const nid = nextQuery.data?.id ?? prevQuery.data?.id;
        if (nid != null) {
          goTo(nid);
        } else {
          const next = new URLSearchParams();
          next.set('sortBy', sortBy);
          next.set('order', order);
          setSearchParams(next, { replace: true });
        }
      },
    });
  };

  const handleAdd = (): void => {
    if (!confirmLeave()) return;
    setNewFormSeq((n) => n + 1);
    const next = new URLSearchParams();
    next.set('mode', 'new');
    next.set('sortBy', sortBy);
    next.set('order', order);
    setSearchParams(next);
  };

  /**
   * 「取消」真正執行的動作(REQ-20260916004)。
   *
   * - **新增模式**:退回剛剛看的那一筆;沒有的話只留 sortBy / order,
   *   交給既有的 auto-load effect 抓第一筆。
   * - **檢視模式**:`viewFormSeq` +1 讓 `<SupplierForm>` 換 key 重新掛載,
   *   表單會從 react-query 快取裡的 `current` 重新帶值 = 還原(理由見 viewFormSeq)。
   *
   * 既有的 confirmLeave()(上一筆 / 下一筆 / 新增 / 搜尋挑選的離開攔截)是另一條路徑,
   * 本次刻意沒有動它。
   */
  const doCancel = (): void => {
    setSubmitError(null);
    setSavedFlash(null);
    setDirty(false);
    if (mode === 'new') {
      const back = lastViewedIdRef.current;
      if (back != null) {
        goTo(back);
      } else {
        const next = new URLSearchParams();
        next.set('sortBy', sortBy);
        next.set('order', order);
        setSearchParams(next);
      }
      return;
    }
    setViewFormSeq((n) => n + 1);
  };

  /**
   * 有未儲存變更才問一次。用 ConfirmDialog 不用 window.confirm ——
   * REQ-20260903009 / REQ-20260907004 立下的規矩。
   */
  const handleCancel = (): void => {
    if (!dirty) {
      doCancel();
      return;
    }
    setCancelConfirm(true);
  };

  const handlePick = (pickedId: number): void => {
    if (!confirmLeave()) return;
    setSearchOpen(false);
    goTo(pickedId);
  };

  // REQ-20260803001:後端 409 的訊息會指名欄位(suppliers.service.ts mapPrismaError),
  // 用關鍵字挑出要標紅框的那一欄;上方的 submitError 提示照舊顯示,兩者並存。
  const conflictField: 'code' | null = submitError?.includes('廠商編號') ? 'code' : null;

  const submitting = createMutation.isPending || updateMutation.isPending;
  const titleText =
    mode === 'new' ? '新增廠商' : current ? current.simpleName : '廠商';

  return (
    <section className="grid gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap border-b pb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <h1 className="text-xl font-semibold truncate">{titleText}</h1>
          {mode === 'view' && current?.code ? (
            <span className="font-mono text-slate-500 text-sm shrink-0">{current.code}</span>
          ) : null}
          {dirty ? (
            <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 shrink-0">
              未儲存
            </span>
          ) : null}
        </div>
        <div className="shrink-0 flex gap-1 items-center">
          <button
            type="button"
            onClick={handleAdd}
            className="border rounded px-2 py-1 text-sm hover:bg-slate-100"
          >
            + 新增
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={submitting}
            className={
              dirty
                ? 'bg-blue-600 text-white rounded px-3 py-1 text-sm hover:bg-blue-700 disabled:opacity-50'
                : 'border rounded px-3 py-1 text-sm hover:bg-slate-100 disabled:opacity-50'
            }
          >
            {submitting ? '儲存中…' : '儲存'}
          </button>
          {mode === 'view' && id != null ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="border border-red-600 text-red-600 rounded px-2 py-1 text-sm hover:bg-red-50 disabled:opacity-50"
            >
              刪除
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleCancel}
            disabled={!(mode === 'new' || dirty)}
            className="border rounded px-2 py-1 text-sm hover:bg-slate-100 disabled:opacity-30"
            data-testid="btn-cancel"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handlePrev}
            disabled={mode !== 'view' || prevQuery.data?.id == null}
            className="border rounded px-2 py-1 text-sm hover:bg-slate-100 disabled:opacity-30"
            title="上一筆"
          >
            ← 上一筆
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={mode !== 'view' || nextQuery.data?.id == null}
            className="border rounded px-2 py-1 text-sm hover:bg-slate-100 disabled:opacity-30"
            title="下一筆"
          >
            下一筆 →
          </button>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="border border-blue-600 text-blue-600 rounded px-2 py-1 text-sm hover:bg-blue-50"
          >
            搜尋
          </button>
        </div>
      </div>

      {submitError ? (
        <div role="alert" className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
          {submitError}
        </div>
      ) : null}
      {savedFlash ? (
        <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
      ) : null}

      {mode === 'view' && supplierQuery.isLoading ? (
        <div className="text-slate-500">載入中...</div>
      ) : mode === 'view' && (supplierQuery.isError || !current) && id != null ? (
        <div role="alert" className="text-red-600">找不到廠商</div>
      ) : (
        <>
          <SupplierForm
            key={mode === 'new' ? `new-${newFormSeq}` : `view-${id ?? 'none'}-${viewFormSeq}`}
            ref={formRef}
            defaultValues={mode === 'view' ? current : undefined}
            autoFocusCode={mode === 'new'}
            onSubmit={handleSubmit}
            onDirtyChange={setDirty}
            conflictField={conflictField}
            conflictMessage={submitError}
          />

          {/* REQ-20260918002:原本是「訪談紀錄」「應付沖帳」兩塊卡片上下堆疊,
              改成與客戶操作台同一排分頁 —— 提醒事項要跟它們在同一排才找得到。
              只動外層容器,`InterviewTimeline` 與 `SupplierApPanel` 的呼叫參數一字未改。 */}
          {mode === 'view' && current ? (
            <div>
              <div className="flex justify-end gap-1 border-b mt-2" data-testid="supplier-console-tabs">
                <TabButton
                  active={activeTab === 'reminders'}
                  onClick={() => setActiveTab('reminders')}
                  testId="tab-supplier-reminders"
                  tone={
                    openRemindersQuery.data && openRemindersQuery.data.length > 0
                      ? 'alert'
                      : 'default'
                  }
                >
                  🔔 提醒事項
                  {openRemindersQuery.data != null ? ` (${openRemindersQuery.data.length})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'interviews'}
                  onClick={() => setActiveTab('interviews')}
                  testId="tab-supplier-interviews"
                >
                  訪談紀錄
                  {interviewsTotalQuery.data != null ? ` (${interviewsTotalQuery.data})` : ''}
                </TabButton>
                <TabButton
                  active={activeTab === 'ap'}
                  onClick={() => setActiveTab('ap')}
                  testId="tab-supplier-ap"
                >
                  應付沖帳
                </TabButton>
              </div>
              <div className="pt-3">
                {activeTab === 'reminders' ? (
                  <ReminderPanel ownerType="supplier" ownerId={current.id} />
                ) : activeTab === 'interviews' ? (
                  /* REQ-20260525001:廠商訪談紀錄(與客戶訪談平行,UI 元件共用 ownerType) */
                  <InterviewTimeline
                    ownerId={current.id}
                    ownerType="supplier"
                    defaultContact={current.boss ?? current.contact1 ?? null}
                  />
                ) : (
                  <SupplierApPanel supplierId={current.id} />
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      <SupplierSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={handlePick}
      />

      <ConfirmDialog
        open={cancelConfirm}
        title="放棄未儲存的變更?"
        message="剛剛輸入但還沒儲存的內容會全部消失,已經存進系統的資料不受影響。"
        confirmLabel="放棄變更"
        cancelLabel="繼續編輯"
        tone="danger"
        onConfirm={doCancel}
        onClose={() => setCancelConfirm(false)}
      />
    </section>
  );
}
